import { useMemo, useState } from "react";
import { ISupplierResult } from "../interfaces/supplier";
import { useMessageApi } from "../context/MessageContext";
import { SupplierServices } from "../services/supplier";
import { SupplierFilterValues } from "../pages/supplier";

interface UseChangeSupplierStatusProps {
  isLoading: boolean;
  selectSupplier: ISupplierResult | undefined;
  showModal: (supplier: ISupplierResult) => void;
  closeModal: () => void;
  handleChangeStatus: () => Promise<void>;
  isModalOpenChangeStatus: boolean;
}

export const useChangeSupplierStatus = (
  onSuccess?: () => void
): UseChangeSupplierStatusProps => {
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpenChangeStatus, setIsModalOpenChangeStatus] = useState(false);
  const [selectSupplier, setSelectSupplier] = useState<ISupplierResult>();

  const messageApi = useMessageApi();

  const changeStatus = async (supplier: ISupplierResult): Promise<boolean> => {
    if (!supplier?.id) return false;
    setIsLoading(true);
    try {
      const res = await SupplierServices.updateStatus({
        supplierId: Number(supplier.id),
        isActive: !supplier.isActive,
      });

      if (res?.success) {
        messageApi.success(res?.message || "Update successful");
        onSuccess?.();
        return true;
      }
      return true;
    } catch (err: any) {
      messageApi.error(err?.message || "Something went wrong");
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const showModal = (supplier: ISupplierResult) => {
    setSelectSupplier(supplier);
    setIsModalOpenChangeStatus(true);
  };

  const closeModal = () => {
    setIsModalOpenChangeStatus(false);
  };

  const handleChangeStatus = async () => {
    if (selectSupplier) {
      const result = await changeStatus(selectSupplier);
      if (result) setIsModalOpenChangeStatus(false);
    }
  };

  return {
    isLoading,
    selectSupplier,
    showModal,
    closeModal,
    handleChangeStatus,
    isModalOpenChangeStatus,
  };
};

export const useActiveSupplierFilterCount = (filters?: SupplierFilterValues): number => {
  return useMemo(() => {
    if (!filters) return 0;

    const validFilterKeys: (keyof SupplierFilterValues)[] = [
      "isActive",
      "isCreditSupplier",
      "apOwners",
      "productOwners",
      "isPartOfGroup",
      "groupName",
      "countrys",
      "currencys",
      "nominalAccountDebits",
      "nominalAccountCredits",
    ];

    return validFilterKeys.reduce((count, key) => {
      const value = filters[key];
      if (Array.isArray(value)) {
        return count + (value.length > 0 ? 1 : 0);
      }
      return count + (value !== undefined && value !== "" ? 1 : 0);
    }, 0);
  }, [filters]);
};
